import Vue from 'vue'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import axios from 'axios'
import { resetAuth } from '@/utils/authInitializer'
import { GOOGLE_CONNECTION, freshLoginProof, requestError } from '@/utils/googleMigration'

let instance
export const getInstance = () => instance
const TARGET_STORAGE_KEY = 'arc.accountSetupTarget'

function localTarget (target) {
  return typeof target === 'string' && target.startsWith('/') && !target.startsWith('//') && !target.includes('\\') && !/^\/(login|register)([/?#]|$)/.test(target)
    ? target
    : '/'
}

function savedTarget () {
  try {
    const target = window.sessionStorage.getItem(TARGET_STORAGE_KEY)
    return target ? localTarget(target) : null
  } catch (error) { return null }
}

function saveTarget (target) {
  try {
    if (target) window.sessionStorage.setItem(TARGET_STORAGE_KEY, localTarget(target))
    else window.sessionStorage.removeItem(TARGET_STORAGE_KEY)
  } catch (error) { /* Navigation still works if browser storage is unavailable. */ }
}

export const useAuth0 = ({
  onRedirectCallback = () => {},
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance
  instance = new Vue({
    data () {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        idTokenClaims: null,
        freshLogin: null,
        targetUrl: savedTarget(),
        auth0Client: null,
        popupOpen: false,
        error: null,
        role: null
      }
    },
    watch: { targetUrl: saveTarget },
    methods: {
      getAuthData () {
        return {
          isAuthenticated: this.isAuthenticated,
          user: this.user,
          auth0Client: this.auth0Client,
          popupOpen: this.popupOpen,
          error: this.error,
          role: this.role
        }
      },
      async loadSession (appState) {
        this.isAuthenticated = await this.auth0Client.isAuthenticated()
        this.user = await this.auth0Client.getUser() || {}
        this.idTokenClaims = await this.auth0Client.getIdTokenClaims()
        this.freshLogin = null
        if (appState) {
          this.targetUrl = localTarget(appState.targetUrl)
          if (appState.arcGoogleLoginStartedAt !== undefined) {
            // SDK 2.0.4 does not reliably enforce per-request max_age: 0.
            this.freshLogin = freshLoginProof(this.idTokenClaims, appState.arcGoogleLoginStartedAt)
          }
        }
        if (this.isAuthenticated) this.role = await this.checkUserRole(this.user.email)
      },
      loginOptions (o = {}) {
        return {
          ...o,
          appState: {
            ...o.appState,
            targetUrl: localTarget(o.appState?.targetUrl || window.location.pathname + window.location.search + window.location.hash),
            arcGoogleLoginStartedAt: Math.floor(Date.now() / 1000)
          },
          authorizationParams: {
            ...o.authorizationParams,
            connection: GOOGLE_CONNECTION,
            prompt: 'login',
            max_age: 0
          }
        }
      },
      async loginWithRedirect (o = {}) {
        const request = this.loginOptions(o)
        resetAuth(Vue)
        this.freshLogin = null
        this.error = null
        this.loading = true
        try {
          await this.auth0Client.loginWithRedirect(request)
        } catch (error) {
          this.error = requestError(error, 'Google sign-in could not start. Please try again.')
          this.loading = false
        }
      },
      async loginWithPopup (o = {}, config) {
        const request = this.loginOptions(o)
        resetAuth(Vue)
        this.freshLogin = null
        this.loading = true
        this.popupOpen = true
        this.error = null
        try {
          await this.auth0Client.loginWithPopup(request, config)
          await this.loadSession(request.appState)
        } catch (error) {
          this.error = requestError(error, 'Google sign-in could not finish. Please try again.')
        } finally {
          this.popupOpen = false
          this.loading = false
        }
      },
      async handleRedirectCallback () {
        this.loading = true
        this.error = null
        let appState
        try {
          const callback = await this.auth0Client.handleRedirectCallback()
          appState = callback.appState
          await this.loadSession(appState)
        } catch (error) {
          this.error = requestError(error, 'Google sign-in could not finish. Please sign in again.')
        } finally {
          const url = new URL(window.location.href)
          for (const key of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(key)
          window.history.replaceState({}, document.title, url.pathname + url.search + url.hash)
          this.loading = false
        }
        // Identity is loaded before ARC initialization runs in the callback.
        if (!this.error) await onRedirectCallback({ ...appState, targetUrl: this.targetUrl })
      },
      getIdTokenClaims (o) { return this.auth0Client.getIdTokenClaims(o) },
      getTokensSilently (o) { return this.auth0Client.getTokenSilently(o) },
      getTokenWithPopup (o) { return this.auth0Client.getTokenWithPopup(o) },
      logout (o) {
        resetAuth(Vue)
        this.targetUrl = null
        saveTarget(null)
        this.freshLogin = null
        this.idTokenClaims = null
        this.user = {}
        this.isAuthenticated = false
        return this.auth0Client.logout(o)
      },
      async checkUserRole (email) {
        try {
          const { data } = await axios({
            method: 'POST',
            url: `https://${process.env.VUE_APP_AUTH0_DOMAIN}/oauth/token`,
            timeout: 15000,
            data: new URLSearchParams({
              client_id: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_CLIENT_ID,
              client_secret: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_CLIENT_SECRET,
              audience: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_AUDIENCE,
              grant_type: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_GRANT_TYPE
            }),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          })
          const managementClient = axios.create({
            baseURL: process.env.VUE_APP_AUTH0_MANAGEMENT_API_SERVICE_AUDIENCE,
            timeout: 15000,
            headers: { Authorization: `Bearer ${data.access_token}` }
          })
          const { data: roles } = await managementClient.get('roles')
          const adminRole = roles.find(role => role.name === 'arc-admin')
          if (!adminRole) return 'regular'
          const { data: adminUsers } = await managementClient.get(`roles/${adminRole.id}/users`)
          return adminUsers.some(user => user.email === email) ? 'admin' : 'regular'
        } catch (error) {
          console.warn('ARC role lookup is unavailable.')
          return false
        }
      }
    },
    async created () {
      try {
        this.auth0Client = await createAuth0Client({
          ...options,
          authorizationParams: { redirect_uri: redirectUri, ...options.authorizationParams }
        })
        const query = new URLSearchParams(window.location.search)
        if (query.has('state') && (query.has('code') || query.has('error'))) {
          await this.handleRedirectCallback()
        } else {
          await this.loadSession()
        }
      } catch (error) {
        this.error = requestError(error, 'Authentication is unavailable. Please sign in again.')
      } finally {
        this.loading = false
      }
    }
  })
  return instance
}

export const Auth0Plugin = {
  install (Vue, options) {
    Vue.prototype.$auth = useAuth0(options)
  }
}
